import { useState } from 'react';
import { Dialog, DialogType, DialogFooter, PrimaryButton, DefaultButton, SpinButton, Text, MessageBar, MessageBarType, Spinner, SpinnerSize } from '@fluentui/react';
import type { MissionControlEnvironment } from '../../types/environment';
import { extendDevBox } from '../../services/environments';

interface ExtendDevBoxDialogProps {
  env: MissionControlEnvironment;
  onDismiss: () => void;
  onExtended: () => void;
}

export default function ExtendDevBoxDialog({ env, onDismiss, onExtended }: ExtendDevBoxDialogProps) {
  const [days, setDays] = useState(7);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const currentExpiry = env.ExpiresOn ? new Date(env.ExpiresOn) : null;
  const projectedExpiry = currentExpiry ? new Date(currentExpiry.getTime() + days * 86_400_000) : null;

  async function handleExtend() {
    setSubmitting(true);
    setError('');
    try {
      const result = await extendDevBox(env.EnvironmentId, days);
      if (result.Result === 'Blocked') {
        setError('Extension limit reached. This DevBox has already been extended the maximum number of times (2).');
      } else {
        onExtended();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to extend DevBox');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      hidden={false}
      onDismiss={onDismiss}
      dialogContentProps={{
        type: DialogType.normal,
        title: 'Extend DevBox',
        subText: `Extend the expiration of "${env.FriendlyName}". Each DevBox can be extended up to 2 times.`,
      }}
      modalProps={{ isBlocking: submitting }}
    >
      {error && <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError('')}>{error}</MessageBar>}

      {currentExpiry && (
        <Text variant="small" styles={{ root: { color: 'var(--ab-text-secondary)', display: 'block', marginBottom: 12 } }}>
          Current expiration: {currentExpiry.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </Text>
      )}

      <SpinButton
        label="Days to extend"
        min={1}
        max={10}
        value={String(days)}
        onChange={(_, val) => val && setDays(Math.min(10, Math.max(1, parseInt(val) || 1)))}
        onIncrement={(val) => { const n = Math.min(10, parseInt(val) + 1); setDays(n); return String(n); }}
        onDecrement={(val) => { const n = Math.max(1, parseInt(val) - 1); setDays(n); return String(n); }}
      />

      {projectedExpiry && (
        <Text variant="small" styles={{ root: { color: 'var(--ab-success)', display: 'block', marginTop: 8 } }}>
          New expiration: {projectedExpiry.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </Text>
      )}

      <DialogFooter>
        <PrimaryButton
          text={submitting ? 'Extending…' : `Extend by ${days} day${days !== 1 ? 's' : ''}`}
          onClick={handleExtend}
          disabled={submitting}
          onRenderIcon={submitting ? () => <Spinner size={SpinnerSize.xSmall} /> : undefined}
        />
        <DefaultButton text="Cancel" onClick={onDismiss} disabled={submitting} />
      </DialogFooter>
    </Dialog>
  );
}
